/**
 * RequiredFieldsPrompt
 *
 * Popup que abre ao ENTRAR numa conversa quando o contato tem campos
 * obrigatórios pendentes na etapa/funil atual (a mesma regra do chip
 * RequiredFieldsSignal: GET /api/custom-fields/contacts/{key}/required).
 *
 * Não bloqueia nada: "Pular" fecha, e o popup volta na próxima abertura da
 * conversa (o pular vale só para esta abertura, nada é persistido). "Salvar"
 * grava pelo mesmo PUT do drawer do contato. Mudança de etapa, agente,
 * automação e régua seguem livres.
 *
 * Componente compartilhado pelos 4 inboxes; o ChatWindow só passa a chave do
 * contato (telefone ou 'instagram:{igsid}' / 'messenger:{psid}').
 * Flag: REQUIRED_FIELDS_POPUP_ENABLED (features.required_fields_popup).
 *
 * Props:
 *   - contactKey  -> chave do contato da conversa aberta
 *   - colors      -> theme (useTheme)
 *   - onSaved     -> (opcional) chamado após salvar
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import customFieldsService from '../../services/global/customFieldsService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { FieldRow } from './CustomFieldsPanel';

const RequiredFieldsPrompt = ({ contactKey, colors, onSaved }) => {
  const { flags } = useFeatureFlags();
  const enabled = Boolean(flags?.required_fields_popup);

  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);   // definições pendentes
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);

  // Uma checagem por abertura de conversa (troca de contactKey). Trocar de
  // contato fecha o popup do anterior na hora.
  useEffect(() => {
    const seq = ++reqRef.current;
    setOpen(false);
    setFields([]);
    setValues({});
    setError(null);
    if (!enabled || !contactKey) return undefined;
    (async () => {
      try {
        const status = await customFieldsService.getContactRequiredStatus(contactKey);
        const missingIds = (status?.missing || []).map((m) => m.id);
        if (seq !== reqRef.current || missingIds.length === 0) return;
        const data = await customFieldsService.getContactFields(contactKey);
        if (seq !== reqRef.current) return;
        const byId = new Map((data.definitions || []).map((d) => [d.id, d]));
        const defs = missingIds.map((id) => byId.get(id)).filter(Boolean);
        if (!defs.length) return;
        setFields(defs);
        setValues(data.values || {});
        setOpen(true);
      } catch (err) {
        // Sinalização best-effort: falha não abre popup nem atrapalha a conversa.
      }
    })();
    return undefined;
  }, [enabled, contactKey]);

  if (!open || !fields.length) return null;

  const close = () => { setOpen(false); setError(null); };

  const handleSave = async () => {
    const payload = {};
    fields.forEach((d) => {
      if (values[d.id] !== undefined) payload[d.id] = values[d.id];
    });
    if (!Object.keys(payload).length) { close(); return; }
    setSaving(true);
    setError(null);
    try {
      await customFieldsService.setContactFields(contactKey, payload);
      onSaved?.();
      close();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível salvar os campos.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 1250,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Campos obrigatórios"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440, maxWidth: '100%', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          backgroundColor: colors.bgPrimary, borderRadius: 14,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <AlertTriangle size={18} color="#F59E0B" style={{ flexShrink: 0 }} />
          <h3 style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
            Campos obrigatórios
          </h3>
          <button
            type="button"
            onClick={close}
            title="Fechar"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: colors.textSecondary, padding: 4, display: 'flex',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.4 }}>
            Preencha para esta etapa do funil.
          </p>
          {fields.map((def) => (
            <FieldRow
              key={def.id}
              def={def}
              colors={colors}
              value={values[def.id]}
              saving={false}
              saved={false}
              onLocalChange={(v) => setValues((prev) => ({ ...prev, [def.id]: v }))}
              onCommit={(v) => setValues((prev) => ({ ...prev, [def.id]: v }))}
            />
          ))}
          {error && (
            <div style={{ fontSize: 12, color: '#DC2626' }}>{error}</div>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap',
          padding: '12px 16px', borderTop: `1px solid ${colors.borderColor}`,
        }}>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            style={{
              padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `1px solid ${colors.borderColor}`, background: 'transparent',
              color: colors.textPrimary, cursor: 'pointer',
            }}
          >
            Pular
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: 'none', backgroundColor: colors.accentPrimary, color: '#fff',
              cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
            }}
          >
            {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RequiredFieldsPrompt;
